/** Everything here is fed straight from the API, so every input may be missing. */

export const EM_DASH = '—';

type Numeric = number | null | undefined;

function isNumber(value: Numeric): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Milliseconds with a unit that suits the magnitude. Sub-second stays in ms. */
export function formatDuration(ms: Numeric): string {
  if (!isNumber(ms)) return EM_DASH;
  const abs = Math.abs(ms);
  if (abs < 1) return `${ms.toFixed(1)} ms`;
  if (abs < 1000) return `${Math.round(ms)} ms`;
  if (abs < 60_000) return `${(ms / 1000).toFixed(2)} s`;

  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;

  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/** Compact form for dense spots like timeline connectors. */
export function formatDurationShort(ms: Numeric): string {
  if (!isNumber(ms)) return EM_DASH;
  const abs = Math.abs(ms);
  if (abs < 1000) return `${Math.round(ms)}ms`;
  if (abs < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

/** Takes a 0..1 fraction, not an already-multiplied percentage. */
export function formatPercent(fraction: Numeric, digits = 1): string {
  if (!isNumber(fraction)) return EM_DASH;
  return `${(fraction * 100).toFixed(digits)}%`;
}

export function formatInteger(value: Numeric): string {
  if (!isNumber(value)) return EM_DASH;
  return Math.round(value).toLocaleString();
}

function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toEpoch(iso: string | null | undefined): number | null {
  const date = parseDate(iso);
  return date === null ? null : date.getTime();
}

/** Full local timestamp, used for `title` tooltips on relative times. */
export function formatAbsolute(iso: string | null | undefined): string {
  const date = parseDate(iso);
  if (date === null) return EM_DASH;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** HH:MM:SS.mmm — retries land within seconds, so milliseconds matter. */
export function formatClock(iso: string | null | undefined): string {
  const date = parseDate(iso);
  if (date === null) return EM_DASH;
  const time = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  return `${time}.${String(date.getMilliseconds()).padStart(3, '0')}`;
}

export function formatRelativeTime(iso: string | null | undefined, now: number = Date.now()): string {
  const epoch = toEpoch(iso);
  if (epoch === null) return EM_DASH;

  const deltaMs = now - epoch;
  if (deltaMs < 0) return formatCountdown(-deltaMs);
  if (deltaMs < 5_000) return 'just now';

  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  return formatAbsolute(iso);
}

/** "in 3s" for a scheduled retry that has not fired yet. */
export function formatCountdown(ms: Numeric): string {
  if (!isNumber(ms) || ms <= 0) return 'due now';
  if (ms < 60_000) return `in ${Math.ceil(ms / 1000)}s`;
  return `in ${Math.ceil(ms / 60_000)}m`;
}

const FAILURE_KIND_LABELS: Record<string, string> = {
  http_status: 'HTTP status',
  timeout: 'Timeout',
  connection_error: 'Connection error',
  unknown: 'Unknown error',
};

export function formatFailureKind(kind: string | null | undefined): string {
  if (!kind) return 'Failed';
  return FAILURE_KIND_LABELS[kind] ?? kind;
}

const DELIVERY_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  succeeded: 'Succeeded',
  dead_lettered: 'Dead-lettered',
};

export function formatDeliveryStatus(status: string | null | undefined): string {
  if (!status) return 'Unknown';
  return DELIVERY_STATUS_LABELS[status] ?? status;
}

/** Pretty-prints any JSON-serialisable value, degrading rather than throwing. */
export function formatJson(value: unknown): string {
  if (value === undefined) return 'undefined';
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}
